import * as THREE from "three";
import {
  WallType,
  SymbolType,
  isDoorType,
  isSlidingDoorType,
  isDoubleDoorType,
  isWindowType,
  isVoidType,
  isSingleLeafDoorType,
} from "../types/floorplannerTypes";
import {
  arcLengthToT,
  getWallArcLength,
  getQuadraticBezierPoint,
} from "./arcUtils";

export interface SymbolInsertGeometry {
  /** Start of the straight-insert section on the wall centerline */
  pA: THREE.Vector2;
  /** End of the straight-insert section on the wall centerline */
  pB: THREE.Vector2;
  /** Midpoint of the insert — used as the symbol's world position */
  midpoint: THREE.Vector2;
  /** Normalized chord direction of the insert (symbol rotation axis) */
  tangent: THREE.Vector2;
  /** Bézier t at pA */
  tA: number;
  /** Bézier t at pB */
  tB: number;
  /** Half the total opening width */
  halfWidth: number;
  /** Offset from positionFromStart to the center of the opening (door hinge compensation) */
  centerOffset: number;
  /** Total opening width */
  totalWidth: number;
  /** Arc-length position of the opening center from wall start */
  sCenter: number;
  /** Arc-length position of the opening start from wall start */
  sA: number;
  /** Arc-length position of the opening end from wall start */
  sB: number;
}

/**
 * Returns the opening (clipping) width and the offset from positionFromStart
 * to the center of the opening for a given symbol type.
 *
 * These values match the gap logic in outlineSegmentation and the clipping
 * rectangle logic in renderStore.getClippingPolygon so all three systems
 * remain consistent.
 */
export function getSymbolWidthInfo(
  symbol: SymbolType,
  defaultFrameWidth: number,
): { totalWidth: number; centerOffset: number } {
  /**
   * Sliding doors use a double-wide wall opening (leaf + clear passage beside it).
   * `doorWidth` is the leaf / passage width; total clear span is 2 × doorWidth.
   */
  if (isSlidingDoorType(symbol)) {
    const frameWidth = symbol.doorFrameWidth ?? defaultFrameWidth;
    return {
      totalWidth: symbol.doorWidth * 2 + frameWidth * 2,
      centerOffset: symbol.doorWidth,
    };
  }
  if (isDoorType(symbol)) {
    const frameWidth = symbol.doorFrameWidth ?? defaultFrameWidth;
    return {
      totalWidth: symbol.doorWidth + frameWidth * 2,
      centerOffset: symbol.doorWidth / 2,
    };
  }
  if (isDoubleDoorType(symbol)) {
    const frameWidth = symbol.doorFrameWidth ?? defaultFrameWidth;
    return {
      totalWidth: symbol.doubleDoorWidth * 2 + frameWidth * 2,
      centerOffset: symbol.doubleDoorWidth,
    };
  }
  if (isWindowType(symbol)) {
    return { totalWidth: symbol.windowLength, centerOffset: 0 };
  }
  if (isVoidType(symbol)) {
    return { totalWidth: symbol.voidLength, centerOffset: 0 };
  }
  return { totalWidth: 0.8, centerOffset: 0 };
}

/**
 * Returns the straight insert width used for rendering/clipping.
 *
 * Straight walls use the nominal opening width. Arc walls use the actual chord
 * length between the insert endpoints so rendered doors line up with the cutout.
 */
export function getSymbolRenderTotalWidth(
  wall: WallType,
  symbol: SymbolType,
  positionFromStart: number,
  defaultFrameWidth: number,
): number {
  const { totalWidth } = getSymbolWidthInfo(symbol, defaultFrameWidth);
  if (wall.lineForm !== "arc" || !wall.controlPoint) {
    return totalWidth;
  }

  const insertGeom = getSymbolInsertGeometry(
    wall,
    symbol,
    positionFromStart,
    defaultFrameWidth,
  );
  return insertGeom.pA.distanceTo(insertGeom.pB);
}

/**
 * Returns the offset from the symbol origin to the center of the rendered
 * straight insert section.
 *
 * For doors, the symbol origin is at the hinge-side blade start, not at the
 * frame edge. Arc walls therefore need a corrected offset derived from the
 * rendered chord width rather than the nominal arc-span width.
 */
export function getSymbolRenderCenterOffset(
  wall: WallType,
  symbol: SymbolType,
  positionFromStart: number,
  defaultFrameWidth: number,
): number {
  if (isSingleLeafDoorType(symbol) || isDoubleDoorType(symbol)) {
    const frameWidth = symbol.doorFrameWidth ?? defaultFrameWidth;
    const renderTotalWidth = getSymbolRenderTotalWidth(
      wall,
      symbol,
      positionFromStart,
      defaultFrameWidth,
    );
    return Math.max((renderTotalWidth - frameWidth * 2) / 2, 0);
  }

  return getSymbolWidthInfo(symbol, defaultFrameWidth).centerOffset;
}

/**
 * Computes the straight-insert geometry for a symbol attached to a wall.
 *
 * For **arc walls**: returns the chord P_a → P_b on the Bézier curve that
 * corresponds to the symbol's opening span. The symbol sits on this chord —
 * a physically straight section of the wall (matching real-world construction
 * where doors/windows are always straight).
 *
 * For **straight walls**: returns the equivalent straight-segment geometry
 * (identical to the existing behavior, expressed in the same interface).
 *
 * @param wall              The wall the symbol is attached to
 * @param symbol            The symbol (Door, Window, Void, …)
 * @param positionFromStart Arc-length from wall start to the symbol's anchor
 *                          (equals positionFromStart stored in SymbolAttachmentType)
 * @param defaultFrameWidth Fallback frame width from the store
 */
export function getSymbolInsertGeometry(
  wall: WallType,
  symbol: SymbolType,
  positionFromStart: number,
  defaultFrameWidth: number,
): SymbolInsertGeometry {
  const { totalWidth, centerOffset } = getSymbolWidthInfo(symbol, defaultFrameWidth);
  const halfWidth = totalWidth / 2;
  const sCenter = positionFromStart + centerOffset;
  const sA = sCenter - halfWidth;
  const sB = sCenter + halfWidth;

  if (wall.lineForm === "arc" && wall.controlPoint) {
    const tA = arcLengthToT(wall, sA);
    const tB = arcLengthToT(wall, sB);
    const pA = getQuadraticBezierPoint(tA, wall.start, wall.controlPoint, wall.end);
    const pB = getQuadraticBezierPoint(tB, wall.start, wall.controlPoint, wall.end);
    const chord = pB.clone().sub(pA);
    const chordLen = chord.length();
    const tangent = chordLen > 1e-6 ? chord.clone().divideScalar(chordLen) : new THREE.Vector2(1, 0);
    const midpoint = pA.clone().add(pB).multiplyScalar(0.5);
    return { pA, pB, midpoint, tangent, tA, tB, halfWidth, centerOffset, totalWidth, sCenter, sA, sB };
  }

  // Straight wall — same math as before, packaged into the shared interface
  const wallDir = new THREE.Vector2().subVectors(wall.end, wall.start);
  const totalLength = wallDir.length();
  const tangent = totalLength > 0 ? wallDir.clone().divideScalar(totalLength) : new THREE.Vector2(1, 0);
  const tA = totalLength > 0 ? Math.max(0, sA / totalLength) : 0;
  const tB = totalLength > 0 ? Math.min(1, sB / totalLength) : 1;
  const pA = wall.start.clone().addScaledVector(tangent, sA);
  const pB = wall.start.clone().addScaledVector(tangent, sB);
  const midpoint = wall.start.clone().addScaledVector(tangent, sCenter);
  return { pA, pB, midpoint, tangent, tA, tB, halfWidth, centerOffset, totalWidth, sCenter, sA, sB };
}

/**
 * Validates whether a symbol can be placed at positionFromStart on the wall
 * without falling off the ends or overlapping an existing attachment.
 *
 * @returns null when valid, or an error description string when invalid.
 */
export function validateSymbolAttachment(
  wall: WallType,
  symbol: SymbolType,
  positionFromStart: number,
  defaultFrameWidth: number,
  existingAttachments: Array<{ symbolId: string; positionFromStart: number; totalWidth: number }>,
  thisSymbolId: string,
): string | null {
  const { totalWidth, centerOffset } = getSymbolWidthInfo(symbol, defaultFrameWidth);
  const halfWidth = totalWidth / 2;
  const sCenter = positionFromStart + centerOffset;
  const sA = sCenter - halfWidth;
  const sB = sCenter + halfWidth;
  const totalWallLength = getWallArcLength(wall);

  if (sA < -0.001 || sB > totalWallLength + 0.001) {
    return "Symbol does not fit on wall (exceeds wall boundaries)";
  }

  for (const existing of existingAttachments) {
    if (existing.symbolId === thisSymbolId) continue;
    const existingHalfWidth = existing.totalWidth / 2;
    // centerOffset for the existing symbol would need the symbol object; here we
    // approximate using the stored positionFromStart as the anchor (not center).
    // The caller performs the full overlap check using getSymbolInsertGeometry.
    const eA = existing.positionFromStart - existingHalfWidth;
    const eB = existing.positionFromStart + existingHalfWidth;
    if (sA < eB && sB > eA) {
      return "Symbol overlaps with an existing attachment";
    }
  }

  return null;
}
